/**
 * BRE-8058: an artifact's "Anyone with the link" page lives at /s/<token>. The
 * token is a bearer secret, so a path that may be logged or tracked goes
 * through `redactPublicLinkPath`, and analytics never start on this path.
 * Pure, so middleware (edge) and client code can both import it.
 */
export const PUBLIC_ARTIFACT_LINK_PATH = "/s";

export function isPublicArtifactLinkPath(pathname: string): boolean {
  return (
    pathname === PUBLIC_ARTIFACT_LINK_PATH ||
    pathname.startsWith(`${PUBLIC_ARTIFACT_LINK_PATH}/`)
  );
}

export function redactPublicLinkPath(pathname: string): string {
  return isPublicArtifactLinkPath(pathname)
    ? `${PUBLIC_ARTIFACT_LINK_PATH}/[token]`
    : pathname;
}

// A link page path inside any text: the token is always 43 base64url characters.
const LINK_TOKEN_IN_TEXT = /\/s\/[A-Za-z0-9_-]{43}/g;

export function redactPublicLinkTokens(text: string): string {
  return text.replace(LINK_TOKEN_IN_TEXT, `${PUBLIC_ARTIFACT_LINK_PATH}/[token]`);
}

type AnalyticsRecord = Record<string, unknown> | undefined;

function redactStrings(record: AnalyticsRecord): AnalyticsRecord {
  if (!record) return record;
  let redacted: Record<string, unknown> | null = null;
  for (const [key, value] of Object.entries(record)) {
    if (typeof value !== "string") continue;
    const scrubbed = redactPublicLinkTokens(value);
    if (scrubbed !== value) (redacted ??= { ...record })[key] = scrubbed;
  }
  return redacted ?? record;
}

/**
 * PostHog `before_send` step for a tab whose SDK is already running: drops an
 * event recorded on a link page, and redacts a link token from the URL-like
 * properties of any other (the SDK remembers the previous page's path and the
 * session's entry URL before this runs).
 */
export function dropPublicLinkPageEvent<
  T extends { properties?: AnalyticsRecord; $set?: AnalyticsRecord; $set_once?: AnalyticsRecord },
>(event: T | null): T | null {
  if (!event) return event;
  const url = event.properties?.$current_url;
  if (typeof url === "string") {
    try {
      if (isPublicArtifactLinkPath(new URL(url).pathname)) return null;
    } catch {
      // Not a URL: nothing to judge the page by, so keep the event.
    }
  }
  return {
    ...event,
    properties: redactStrings(event.properties),
    ...(event.$set ? { $set: redactStrings(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: redactStrings(event.$set_once) } : {}),
  };
}

export function buildPublicArtifactLinkUrl(origin: string, token: string): string {
  return `${origin}${PUBLIC_ARTIFACT_LINK_PATH}/${token}`;
}

/** BRE-8445: the framable page for a public link URL. The app sets
 *  `trailingSlash`, so it ends in a slash to skip a redirect inside the frame. */
export function buildPublicArtifactEmbedUrl(publicUrl: string): string {
  const url = new URL(publicUrl);
  url.pathname = `${url.pathname.replace(/\/+$/, "")}/embed/`;
  return url.toString();
}

const HTML_ATTRIBUTE_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  '"': "&quot;",
  "<": "&lt;",
  ">": "&gt;",
};

/** BRE-8445: the snippet "Copy embed code" copies. */
export function buildPublicArtifactEmbedCode(publicUrl: string, title: string): string {
  const escape = (value: string) => value.replace(/[&"<>]/g, (c) => HTML_ATTRIBUTE_ESCAPES[c]);
  return `<iframe src="${escape(buildPublicArtifactEmbedUrl(publicUrl))}" width="100%" height="600" style="border:0" title="${escape(title)}"></iframe>`;
}
